'use client';

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { currencyFormatter, dateFormatter } from '@web/utils/currency';
import { H2 } from '@web/components/Typography';
import { FlexBox } from '@web/components/flex-box';
import useSWR from 'swr';
import { useParams } from 'next/navigation';
import fetcher from '@web/utils/fetch';
import { subWeeks, subMonths, subYears, isAfter, isEqual } from 'date-fns';
import {
  TimeRangeControls,
  TimeRange,
} from '@web/app/components/TimeRangeControls';

const colors = [
  '#1f77b4', // Muted Blue
  '#ff7f0e', // Safety Orange
  '#2ca02c', // Cooked Asparagus Green
  '#d62728', // Brick Red
  '#9467bd', // Muted Purple
  '#8c564b', // Chestnut Brown
  '#e377c2', // Raspberry Yogurt Pink
  '#7f7f7f', // Middle Gray
  '#bcbd22', // Curry Yellow-Green
  '#17becf', // Blue-Teal
];

const transformData = (offers, offersHistory) => {
  const combinedDataMap = new Map();

  offers?.forEach((offer) => {
    offersHistory[offer.seller_product_url]?.forEach((dataPoint) => {
      if (offer.seller.name.toLowerCase().includes('amazon')) return;
      const existingData = combinedDataMap.get(dataPoint.created_at);
      if (existingData) {
        existingData[offer.seller.name] = dataPoint.base_price;
      } else {
        combinedDataMap.set(dataPoint.created_at, {
          created_at: dataPoint.created_at,
          [offer.seller.name]: dataPoint.base_price,
        });
      }
    });
  });

  const combinedData = Array.from(combinedDataMap.values());
  combinedData.sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  return combinedData;
};

const PriceTrend = ({
  offers,
  isMobile,
  timeRange: initialTimeRange,
}: {
  offers: any;
  isMobile?: boolean;
  timeRange?: TimeRange;
}) => {
  const params = useParams();
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange || '3M');
  
  const { data: offersHistory } = useSWR(
    () => params?.slug && `/api/product/${params?.slug}?timeRange=${timeRange}`,
    fetcher
  );

  const [transformedData, setTransformedData] = useState<any>([]);
  const [selectedSellers, setSelectedSellers] = useState<any>([]);

  const handleSellerChange = (event) => {
    setSelectedSellers(event.target.value);
  };

  const filterDataByTimeRange = (data: any[]) => {
    if (!data?.length) return [];

    const now = new Date();
    const getDateLimit = () => {
      switch (timeRange) {
        case '1W':
          return subWeeks(now, 1);
        case '1M':
          return subMonths(now, 1);
        case '3M':
          return subMonths(now, 3);
        case '6M':
          return subMonths(now, 6);
        case '1Y':
          return subYears(now, 1);
        default:
          return new Date(0);
      }
    };

    const dateLimit = getDateLimit();
    return data.filter((item) => {
      const itemDate = new Date(item.created_at);
      return isAfter(itemDate, dateLimit) || isEqual(itemDate, dateLimit);
    });
  };

  useEffect(() => {
    if (!offersHistory || !offers.length) return;
    const filteredOffers = offers.slice(0, 10);
    const data = transformData(filteredOffers, offersHistory);
    setTransformedData(filterDataByTimeRange(data));
    setSelectedSellers(filteredOffers?.map((offer) => offer.seller.name));
  }, [offers, offersHistory, timeRange]);

  return (
    <>
      <FlexBox flexDirection="column" gap={1}>
        <FlexBox
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
          pb={1}
        >
          <H2 fontSize="1.25rem" sx={{ my: 0 }}>
            Price history
          </H2>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TimeRangeControls value={timeRange} onChange={setTimeRange} />
            <FormControl sx={{ width: 'auto' }}>
              <Select
                multiple
                value={selectedSellers}
                onChange={handleSellerChange}
                renderValue={(selected) =>
                  `${selected.length} sellers selected`
                }
                size="small"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 500,
                      minWidth: 300,
                    },
                  },
                }}
              >
                {offers
                  ?.filter(
                    (offer) =>
                      !offer.seller.name.toLowerCase().includes('amazon')
                  )
                  ?.map((offer, index) => (
                    <MenuItem key={index} value={offer.seller.name}>
                      <Checkbox
                        checked={
                          selectedSellers.indexOf(offer.seller.name) > -1
                        }
                      />
                      <ListItemText primary={offer.seller.name} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </FlexBox>

        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <FlexBox justifyContent="space-between" alignItems="center" pb={1}>
            <H2 fontSize="1.25rem" sx={{ my: 0 }}>
              Price history
            </H2>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignItems: 'flex-end',
              }}
            >
              <FormControl size="small">
                <Select
                  multiple
                  value={selectedSellers}
                  onChange={handleSellerChange}
                  renderValue={(selected) =>
                    `${selected.length} sellers selected`
                  }
                  size="small"
                >
                  {offers
                    ?.filter(
                      (offer) =>
                        !offer.seller.name.toLowerCase().includes('amazon')
                    )
                    ?.map((offer, index) => (
                      <MenuItem key={index} value={offer.seller.name}>
                        <Checkbox
                          checked={
                            selectedSellers.indexOf(offer.seller.name) > -1
                          }
                        />
                        <ListItemText primary={offer.seller.name} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TimeRangeControls value={timeRange} onChange={setTimeRange} />
            </Box>
          </FlexBox>
        </Box>
      </FlexBox>

      <Card pt={4} component={Box}>
        {transformedData && (
          <ResponsiveContainer width="100%" height={isMobile ? 400 : 500}>
            <LineChart
              data={transformedData}
              width={100}
              height={300}
              margin={{
                top: isMobile ? 0 : 20,
                right: 50,
                left: isMobile ? 20 : 50,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="created_at"
                tickFormatter={(value) =>
                  dateFormatter().format(new Date(value))
                }
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={(value) => currencyFormatter.format(value)}
              />
              <Legend />
              <Tooltip
                labelFormatter={(value) =>
                  dateFormatter({
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                  }).format(new Date(value))
                }
                formatter={(value: number, name) => {
                  return [currencyFormatter.format(value), name];
                }}
              />
              {offers
                ?.filter(
                  (offer) => selectedSellers.indexOf(offer.seller.name) > -1
                )
                ?.filter(
                  (offer) => !offer.seller.name.toLowerCase().includes('amazon')
                )
                .map((offer, index) => ({
                  dataKey: offer.seller.name,
                  name: offer.seller.name,
                  color: colors[index],
                }))
                .map((offer, index) => (
                  <Line
                    key={index}
                    type="monotone"
                    dot={false}
                    dataKey={offer.dataKey}
                    isAnimationActive={false}
                    stroke={colors[index]}
                    strokeWidth={3}
                    name={offer.name}
                    activeDot={{ r: 8 }}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </>
  );
};

export default PriceTrend;
